// REQ-P1-06b: アーティスト一覧の契約型付きQueryフック。
// 一覧画面のGETは生成型付きAPIクライアントへ集約し、Queryキャッシュを正本にする。

import { useQuery } from '@tanstack/react-query';
import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type ArtistListQuery = NonNullable<paths['/admin/artists']['get']['parameters']['query']>;
type ArtistListResponse = NonNullable<
  paths['/admin/artists']['get']['responses'][200]['content']
>['application/json'];
type AdminArtistDetailResponse = NonNullable<
  paths['/admin/artists/{id}']['get']['responses'][200]['content']
>['application/json'];
type ArtistGroupListResponse = NonNullable<
  paths['/artist-groups']['get']['responses'][200]['content']
>['application/json'];
type PartListResponse = NonNullable<
  paths['/parts']['get']['responses'][200]['content']
>['application/json'];
type AddPartRequest = NonNullable<
  paths['/parts/add']['post']['requestBody']
>['content']['application/json'];
type UpdatePartRequest = NonNullable<
  paths['/parts/update']['post']['requestBody']
>['content']['application/json'];
type DeletePartRequest = NonNullable<
  paths['/parts/delete']['post']['requestBody']
>['content']['application/json'];
type ReorderPartRequest = NonNullable<
  paths['/parts/reorder']['post']['requestBody']
>['content']['application/json'];
type GroupTagListResponse = NonNullable<
  paths['/group-tags']['get']['responses'][200]['content']
>['application/json'];
type AddGroupTagRequest = NonNullable<
  paths['/group-tags/add']['post']['requestBody']
>['content']['application/json'];
type UpdateGroupTagRequest = NonNullable<
  paths['/group-tags/update']['post']['requestBody']
>['content']['application/json'];
type DeleteGroupTagRequest = NonNullable<
  paths['/group-tags/delete']['post']['requestBody']
>['content']['application/json'];
type ReorderGroupTagRequest = NonNullable<
  paths['/group-tags/reorder']['post']['requestBody']
>['content']['application/json'];
type CreateArtistGroupRequest = NonNullable<
  paths['/groups/create']['post']['requestBody']
>['content']['application/json'];
type RenameArtistGroupRequest = NonNullable<
  paths['/groups/rename']['post']['requestBody']
>['content']['application/json'];
type GroupArtistMutationRequest = NonNullable<
  paths['/groups/add-artist']['post']['requestBody']
>['content']['application/json'];
type GroupArtistsMutationRequest = NonNullable<
  paths['/groups/add-multiple-artists']['post']['requestBody']
>['content']['application/json'];
type UpdateAdminArtistWithholdingRequest = NonNullable<
  paths['/admin/artists/{id}/withholding']['post']['requestBody']
>['content']['application/json'];
type UpdateAdminArtistWithholdingResponse = NonNullable<
  paths['/admin/artists/{id}/withholding']['post']['responses'][200]['content']
>['application/json'];
type AdminArtistContractHistoryQuery = NonNullable<
  paths['/admin/artists/{artistId}/contracts']['get']['parameters']['query']
>;
type AdminArtistContractHistoryResponse = NonNullable<
  paths['/admin/artists/{artistId}/contracts']['get']['responses'][200]['content']
>['application/json'];
type AdminArtistMembershipsResponse = NonNullable<
  paths['/admin/artists/{id}/memberships']['get']['responses'][200]['content']
>['application/json'];
type AdminArtistBankInfoResponse = NonNullable<
  paths['/admin/artist/{id}/bank-info']['get']['responses'][200]['content']
>['application/json'];
type AdminArtistPhotosResponse = NonNullable<
  paths['/admin/artist/{id}/photos']['get']['responses'][200]['content']
>['application/json'];
type UpdateAdminArtistBasicInfoRequest = NonNullable<
  paths['/admin/artists/{id}/update']['post']['requestBody']
>['content']['application/json'];
type UpdateAdminArtistNoteRequest = NonNullable<
  paths['/admin/artists/{id}/note']['post']['requestBody']
>['content']['application/json'];
type RequestAdminArtistBanRequest = NonNullable<
  paths['/admin/artists/{id}/ban-request']['post']['requestBody']
>['content']['application/json'];
type ArtistEmailAvailableQuery = NonNullable<
  paths['/artist/email-available']['get']['parameters']['query']
>;
type ArtistEmailAvailableResponse = NonNullable<
  paths['/artist/email-available']['get']['responses'][200]['content']
>['application/json'];
type RegisterArtistRequest = NonNullable<
  paths['/admin/register-artist']['post']['requestBody']
>['content']['application/json'];
type RegisterArtistResponse = NonNullable<
  paths['/admin/register-artist']['post']['responses'][200]['content']
>['application/json'];
type ArtistInviteLinksResponse = NonNullable<
  paths['/admin/artist-invite-links']['get']['responses'][200]['content']
>['application/json'];
type CreateArtistInviteLinkRequest = NonNullable<
  paths['/admin/artist-invite-links']['post']['requestBody']
>['content']['application/json'];
type CreateArtistInviteLinkResponse = NonNullable<
  paths['/admin/artist-invite-links']['post']['responses'][200]['content']
>['application/json'];

export type ArtistListRow = ArtistListResponse['artists'][number];
export type ArtistGroupRow = ArtistGroupListResponse[number];
export type PartRow = PartListResponse[number];
export type GroupTagRow = GroupTagListResponse[number];

function ensureOk(response: Response) {
  if (!response.ok) throw new ApiStatusError(response.status);
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const message = record.message || record.error;
        return typeof message === 'string' ? message : error;
      }
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

async function readJsonObject(response: Response): Promise<Record<string, unknown>> {
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

async function ensureOkWithMessage(
  result: { error?: unknown; response: Response },
  fallbackMessage: string
) {
  if (result.response.ok) return;
  const payload = await readJsonObject(result.response);
  const message =
    (typeof payload.message === 'string' && payload.message) ||
    (typeof payload.error === 'string' && payload.error) ||
    getApiErrorMessage(result.error) ||
    fallbackMessage;
  const statusError = new ApiStatusError(result.response.status);
  statusError.message = message;
  throw statusError;
}

export async function listAdminArtists(query: ArtistListQuery): Promise<ArtistListResponse> {
  const { data, response } = await api.GET('/admin/artists', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const artists = Array.isArray(data.artists) ? data.artists : [];
  const totalCount = Number(data.totalCount);
  return {
    success: true,
    artists,
    totalCount: Number.isFinite(totalCount) ? totalCount : artists.length,
    hasBirthdaysToday: Boolean(data.hasBirthdaysToday),
    paging: data.paging,
  };
}

export async function getAdminArtistDetail(id: string): Promise<AdminArtistDetailResponse> {
  const { data, response } = await api.GET('/admin/artists/{id}', {
    params: { path: { id } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as AdminArtistDetailResponse;
}

export async function updateAdminArtistWithholding(
  id: string,
  body: UpdateAdminArtistWithholdingRequest
): Promise<UpdateAdminArtistWithholdingResponse> {
  const { data, response } = await api.POST('/admin/artists/{id}/withholding', {
    params: { path: { id } },
    body,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as UpdateAdminArtistWithholdingResponse;
}

export async function listAdminArtistContractHistory(
  artistId: string,
  query: AdminArtistContractHistoryQuery
): Promise<AdminArtistContractHistoryResponse> {
  const { data, response } = await api.GET('/admin/artists/{artistId}/contracts', {
    params: { path: { artistId }, query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as AdminArtistContractHistoryResponse;
}

export async function getAdminArtistMemberships(
  id: string
): Promise<AdminArtistMembershipsResponse> {
  const { data, response } = await api.GET('/admin/artists/{id}/memberships', {
    params: { path: { id } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as AdminArtistMembershipsResponse;
}

export async function getAdminArtistBankInfo(id: string): Promise<AdminArtistBankInfoResponse> {
  const { data, response } = await api.GET('/admin/artist/{id}/bank-info', {
    params: { path: { id } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as AdminArtistBankInfoResponse;
}

export async function listAdminArtistPhotos(id: string): Promise<AdminArtistPhotosResponse> {
  const { data, response } = await api.GET('/admin/artist/{id}/photos', {
    params: { path: { id } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as AdminArtistPhotosResponse;
}

export async function updateAdminArtistBasicInfo(
  id: string,
  body: UpdateAdminArtistBasicInfoRequest
): Promise<void> {
  const result = await api.POST('/admin/artists/{id}/update', {
    params: { path: { id } },
    body,
  });
  await ensureOkWithMessage(result, 'アーティスト情報の更新に失敗しました');
}

export async function updateAdminArtistNote(
  id: string,
  body: UpdateAdminArtistNoteRequest
): Promise<void> {
  const result = await api.POST('/admin/artists/{id}/note', {
    params: { path: { id } },
    body,
  });
  await ensureOkWithMessage(result, '管理メモの保存に失敗しました');
}

export async function downloadAdminArtistPhoto(id: string, photoId: number | string): Promise<Blob> {
  const { data, error, response } = await api.GET('/admin/artist/{id}/photos/{photoId}/download', {
    params: { path: { id, photoId: String(photoId) } },
    parseAs: 'blob',
  });
  if (!response.ok || !data) throwApiError(response.status, error, '宣材写真の取得に失敗しました');
  return data as Blob;
}

export async function deleteAdminArtistPhoto(id: string, photoId: number | string): Promise<void> {
  const result = await api.DELETE('/admin/artist/{id}/photos/{photoId}', {
    params: { path: { id, photoId: String(photoId) } },
  });
  await ensureOkWithMessage(result, '宣材写真の削除に失敗しました');
}

export async function requestAdminArtistBan(
  id: string,
  body?: RequestAdminArtistBanRequest
): Promise<void> {
  const result = await api.POST('/admin/artists/{id}/ban-request', {
    params: { path: { id } },
    ...(body ? { body } : {}),
  });
  await ensureOkWithMessage(result, 'BAN申請に失敗しました');
}

export async function unbanAdminArtist(id: string): Promise<void> {
  const result = await api.POST('/admin/artists/{id}/unban', {
    params: { path: { id } },
  });
  await ensureOkWithMessage(result, 'BAN解除に失敗しました');
}

export async function checkArtistEmailAvailable(
  query: ArtistEmailAvailableQuery
): Promise<ArtistEmailAvailableResponse> {
  const { data, error, response } = await api.GET('/artist/email-available', {
    params: { query },
  });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'メールアドレスの確認に失敗しました');
  }
  return data as ArtistEmailAvailableResponse;
}

export async function registerArtistProvisionally(
  body: RegisterArtistRequest
): Promise<RegisterArtistResponse> {
  const { data, error, response } = await api.POST('/admin/register-artist', { body });
  if (!response.ok || !data) {
    throwApiError(response.status, error, 'アーティストの登録に失敗しました');
  }
  return data as RegisterArtistResponse;
}

export async function listArtistInviteLinks(): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/admin/artist-invite-links');
  if (!response.ok || !data) {
    throwApiError(response.status, error, '限定URL一覧の取得に失敗しました');
  }
  const invites = (data as ArtistInviteLinksResponse).invites;
  return Array.isArray(invites) ? invites : [];
}

export async function createArtistInviteLink(
  body: CreateArtistInviteLinkRequest
): Promise<CreateArtistInviteLinkResponse> {
  const { data, error, response } = await api.POST('/admin/artist-invite-links', { body });
  if (!response.ok || !data) {
    throwApiError(response.status, error, '限定URLの発行に失敗しました');
  }
  return data as CreateArtistInviteLinkResponse;
}

export async function revokeArtistInviteLink(token: string): Promise<void> {
  const { error, response } = await api.DELETE('/admin/artist-invite-links/{token}', {
    params: { path: { token } },
  });
  if (!response.ok) {
    throwApiError(response.status, error, '無効化に失敗しました');
  }
}

export async function listParts(): Promise<PartRow[]> {
  const { data, response } = await api.GET('/parts');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data)
    ? [...data].sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0))
    : [];
}

export async function addPart(body: AddPartRequest): Promise<void> {
  const result = await api.POST('/parts/add', { body });
  await ensureOkWithMessage(result, 'パートの追加に失敗しました');
}

export async function updatePart(body: UpdatePartRequest): Promise<void> {
  const result = await api.POST('/parts/update', { body });
  await ensureOkWithMessage(result, 'パートの更新に失敗しました');
}

export async function deletePart(body: DeletePartRequest): Promise<void> {
  const result = await api.POST('/parts/delete', { body });
  await ensureOkWithMessage(result, 'パートの削除に失敗しました');
}

export async function reorderPart(body: ReorderPartRequest): Promise<void> {
  const result = await api.POST('/parts/reorder', { body });
  await ensureOkWithMessage(result, 'パートの順序変更に失敗しました');
}

export async function listArtistGroups(): Promise<ArtistGroupRow[]> {
  const { data, response } = await api.GET('/artist-groups');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data) ? data : [];
}

export async function listGroupTags(): Promise<GroupTagRow[]> {
  const { data, response } = await api.GET('/group-tags');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data) ? data : [];
}

export async function addGroupTag(body: AddGroupTagRequest): Promise<void> {
  const result = await api.POST('/group-tags/add', { body });
  await ensureOkWithMessage(result, 'グループタグの追加に失敗しました');
}

export async function updateGroupTag(body: UpdateGroupTagRequest): Promise<void> {
  const result = await api.POST('/group-tags/update', { body });
  await ensureOkWithMessage(result, 'グループタグの更新に失敗しました');
}

export async function deleteGroupTag(body: DeleteGroupTagRequest): Promise<void> {
  const result = await api.POST('/group-tags/delete', { body });
  await ensureOkWithMessage(result, 'グループタグの削除に失敗しました');
}

export async function reorderGroupTag(body: ReorderGroupTagRequest): Promise<void> {
  const result = await api.POST('/group-tags/reorder', { body });
  await ensureOkWithMessage(result, 'グループタグの順序変更に失敗しました');
}

export async function createArtistGroup(
  body: CreateArtistGroupRequest
): Promise<ArtistGroupRow> {
  const { data, response } = await api.POST('/groups/create', { body });
  ensureOk(response);
  return (data || {}) as ArtistGroupRow;
}

export async function renameArtistGroup(
  body: RenameArtistGroupRequest
): Promise<ArtistGroupRow> {
  const { data, response } = await api.POST('/groups/rename', { body });
  ensureOk(response);
  return (data || {}) as ArtistGroupRow;
}

export async function deleteArtistGroup(groupId: number) {
  const { response } = await api.DELETE('/groups/delete/{groupId}', {
    params: { path: { groupId } },
  });
  ensureOk(response);
}

export async function addArtistToGroup(body: GroupArtistMutationRequest) {
  const { response } = await api.POST('/groups/add-artist', { body });
  ensureOk(response);
}

export async function addMultipleArtistsToGroup(body: GroupArtistsMutationRequest) {
  const { response } = await api.POST('/groups/add-multiple-artists', { body });
  ensureOk(response);
}

export async function removeArtistFromGroup(body: GroupArtistMutationRequest) {
  const { response } = await api.POST('/groups/remove-artist', { body });
  ensureOk(response);
}

export async function removeMultipleArtistsFromGroup(body: GroupArtistsMutationRequest) {
  const { response } = await api.POST('/groups/remove-multiple-artists', { body });
  ensureOk(response);
}

export async function listGroupArtistIds(groupId: number): Promise<string[]> {
  const result = await api.GET('/groups/{groupId}/artists', {
    params: { path: { groupId } },
  });
  ensureOk(result.response);
  const data = (result.data || {}) as { artists?: unknown };
  return Array.isArray(data.artists) ? data.artists.map((id) => String(id)) : [];
}

export function useAdminArtistsQuery(query: ArtistListQuery) {
  return useQuery({
    queryKey: ['admin-artists', query],
    queryFn: () => listAdminArtists(query),
    placeholderData: (previousData) => previousData,
  });
}

export function useArtistListMastersQuery() {
  return useQuery({
    queryKey: ['artist-list-masters'],
    queryFn: async () => {
      const [parts, groups, groupTags] = await Promise.all([
        listParts(),
        listArtistGroups(),
        listGroupTags(),
      ]);
      return { parts, groups, groupTags };
    },
  });
}
